import { ENV } from '@/constants';
import { exploreSchemaItemList } from '../exploreSchema';

export const schemaObject_PixarAiGenerator = {
	'@context': 'https://schema.org',
	'@graph': [
		{
			'@type': 'WebPage',
			'@id': 'https://liftapp.ai/pixar-ai-generator',
			name: 'Pixar Style AI Generator — Turn Photos into Pixar Art Style | Lift',
			description:
				"Turn your photos into charming Pixar-style characters with Lift's AI generator. No design skills needed — just upload your image and get Pixar art that's perfect for social media. No watermark. Try it online now!",
			url: 'https://liftapp.ai/pixar-ai-generator',
			image: 'https://liftapp.ai/seo-image.webp',
			mainEntity: [
				{ '@id': 'https://liftapp.ai/pixar-ai-generator#product' },
				{ '@id': 'https://liftapp.ai/pixar-ai-generator#howto' },
				{ '@id': 'https://liftapp.ai/pixar-ai-generator#benefits' },
				{ '@id': 'https://liftapp.ai/pixar-ai-generator#faq' },
				{ '@id': 'https://liftapp.ai/pixar-ai-generator#why-lift' },
				{ '@id': 'https://liftapp.ai/pixar-ai-generator#explore-tools' },
				{ '@id': 'https://liftapp.ai/pixar-ai-generator#all-in-one' },
			],
		},
		// product
		{
			'@type': 'Product',
			'@id': 'https://liftapp.ai/pixar-ai-generator#product',
			name: 'Pixar Style AI Generator',
			description:
				"Turn your photos into charming Pixar-style characters with Lift's AI generator. Get that iconic animated look — vibrant colors, expressive faces, and smooth, stylized textures — in seconds, no animation skills required.",
			url: 'https://liftapp.ai/pixar-ai-generator',
			fileFormat: ['image/jpeg', 'image/png', 'image/webp'],
			memoryRequirements: 'Max file size: 20MB',
			brand: {
				'@type': 'Brand',
				name: 'Lift',
			},
			aggregateRating: {
				'@type': 'AggregateRating',
				ratingValue: '4.8',
				reviewCount: 130000,
				bestRating: '5',
				worstRating: '1',
			},
			interactionStatistic: [
				{
					'@type': 'InteractionCounter',
					interactionType: { '@type': 'InstallAction' },
					userInteractionCount: 13000000,
				},
			],
		},
		// howto
		{
			'@type': 'HowTo',
			'@id': 'https://liftapp.ai/pixar-ai-generator#howto',
			name: 'How it works',
			step: [
				{
					'@type': 'HowToStep',
					position: 1,
					name: 'Upload your photos',
					text: "Choose a photo from your device or take one using Lift's app",
				},
				{
					'@type': 'HowToStep',
					position: 2,
					name: '✨ AI applies the Pixar effect',
					text: 'Our AI transforms your photo into a colorful Pixar-style character with that signature animated movie look',
				},
				{
					'@type': 'HowToStep',
					position: 3,
					name: 'Customize adjustments',
					text: 'Fine-tune your image with easy-to-use editing tools',
				},
				{
					'@type': 'HowToStep',
					position: 4,
					name: 'Download and use',
					text: 'Download your image or publish it straight to your social feeds',
				},
			],
		},
		// benefit
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/pixar-ai-generator#benefits',
			name: 'Who can benefit from AI Pixar Generator?',
			description:
				"Lift's AI Pixar Generator is perfect for fans of Pixar films, social media users, content creators, and families who want to create charming Pixar-style characters from their photos.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Pixar superfans wanting to see themselves as animated heroes',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Social media users crafting eye-catching avatars and posts',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Content creators generating scroll-stopping TikTok, IG, and YouTube art',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Parents and families turning portraits into Pixar-style keepsakes',
				},
				{
					'@type': 'ListItem',
					position: 5,
					name: 'Cosplayers and event hosts designing playful invites or merch',
				},
				{
					'@type': 'ListItem',
					position: 6,
					name: 'Anyone needing quick, premium-quality art for gifts or profile refreshes',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/benefits.3da6de53.webp',
				name: 'Pixar Style Characters Generator by Lift',
				description:
					'Example of how Lift AI Pixar Generator transforms photos into charming Pixar-style animated characters.',
			},
		},

		// faq
		{
			'@type': 'FAQPage',
			'@id': 'https://liftapp.ai/pixar-ai-generator#faq',
			mainEntity: [
				{
					'@type': 'Question',
					name: 'What is Lift AI Pixar Generator?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Lift AI Pixar Generator is an online tool that transforms your photos into Pixar-style animated characters — perfect for social media avatars, profile pictures, creative projects, or just for fun.',
					},
				},
				{
					'@type': 'Question',
					name: 'How does Lift AI Pixar Generator work?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Our AI analyzes your photo with deep learning, preserves your facial features and expressions, and reimagines them in Pixar's signature art style with vibrant colors, smooth textures, and expressive cartoon aesthetics. Everything is automatic — no manual editing or animation skills needed.",
					},
				},
				{
					'@type': 'Question',
					name: 'Can I create a Pixar-style character with any photo?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: 'Yes. Lift works with any portrait photo — selfies, professional shots, or casual pics. The AI adapts to your unique features and creates custom transformations inspired by classic Pixar-style aesthetics.',
					},
				},
				{
					'@type': 'Question',
					name: 'Can I download the results without a watermark?',
					acceptedAnswer: {
						'@type': 'Answer',
						text: "Absolutely. All images generated or edited in Lift can be exported without watermark when you're on the Pro plan. Download them in high-res PNG or JPG — ready for social media, print, or personal use.",
					},
				},
			],
		},

		// why lift
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/pixar-ai-generator#why-lift',
			name: "Why Lift's AI Pixar Generator stands out",
			description:
				"Lift's Pixar Generator combines professional-grade AI, ease of use, and affordability — making it a trusted choice for millions of users worldwide.",
			itemListOrder: 'Unordered',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'High-quality results',
					description:
						'AI-powered technology delivers professional-grade visuals every time',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Intelligent rendering',
					description:
						'AI tailors the effect to the scene and subject for optimal results',
				},
				{
					'@type': 'ListItem',
					position: 3,
					name: 'Cross-platform support',
					description: 'Available on iOS, Android, and Web for seamless access',
				},
				{
					'@type': 'ListItem',
					position: 4,
					name: 'Budget-friendly',
					description:
						'Save on costly designers with an affordable and intuitive tool',
				},
			],
			image: {
				'@type': 'ImageObject',
				contentUrl:
					'https://liftapp.ai/_next/static/media/whyUse.ae83a89c.webp',
				name: 'Why use Lift AI Pixar Generator',
				description:
					"Comparison of Lift's AI Pixar Generator benefits — high-quality results, intelligent rendering, multi-platform access, and cost savings.",
			},
		},
		// explore
		{
			'@type': 'ItemList',
			'@id': 'https://liftapp.ai/pixar-ai-generator#explore-tools',
			name: 'Explore the AI tools',
			description:
				'A curated list of AI-powered tools for image generation, enhancement, background removal, and more – all available on Lift.',
			itemListOrder: 'Unordered',
			...exploreSchemaItemList,
		},

		{
			'@type': 'SoftwareApplication',
			'@id': 'https://liftapp.ai/pixar-ai-generator#all-in-one',
			name: 'Try Lift – AI Content Creation Tools',
			description:
				'Your all-in-one tools to create, edit, and elevate your content. Create stunning content with AI tools, templates, and editing features effortlessly.',
			operatingSystem: 'iOS, Android, Web',
			applicationCategory: 'MultimediaApplication',
			url: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
			applicationSubCategory: 'Content Creation',
			publisher: {
				'@type': 'Organization',
				name: 'Lift',
				url: 'https://liftapp.ai',
			},
			potentialAction: [
				{
					'@type': 'ViewAction',
					name: 'Open Web App',
					target: `${ENV.LIFT_WEB_APP_URL}/sign-in?auto_login=true`,
				},
			],
		},

		{
			'@type': 'BreadcrumbList',
			itemListElement: [
				{
					'@type': 'ListItem',
					position: 1,
					name: 'Home',
					item: 'https://liftapp.ai',
				},
				{
					'@type': 'ListItem',
					position: 2,
					name: 'Pixar AI Generator',
					item: 'https://liftapp.ai/pixar-ai-generator',
				},
			],
		},
	],
};
